import { readAttributionSnapshot } from '../components/marketing/utils/utm';
import instance from '../helpers/axiosconfig';
import { sanitizeMarketingUrl, stripIdentifyingMarketingData } from '../utils/gtm';

const SERVER_MIRRORED_CUSTOM_EVENTS = new Set([
  'CTAClick',
  'TrialBookingStart',
  'BookingStep',
  'TeacherSelected',
  'TimeSlotSelected',
  'BookingDetailsSubmitted',
  'OTPVerified',
  'TrialBooked',
  'BookingFailed',
  'PackageOfferViewed',
  'CheckoutStarted',
]);

const STORED_FUNNEL_EVENTS = new Set([
  'PageView',
  'ViewContent',
  'CTAClick',
  'TrialBookingStart',
  'BookingStep',
  'TeacherSelected',
  'TimeSlotSelected',
  'BookingDetailsSubmitted',
  'OTPVerified',
  'TrialBooked',
  'BookingFailed',
  'TrialJoined',
  'TrialAttended',
  'PackageOfferViewed',
  'CheckoutStarted',
  'CheckoutBillingDetailsViewed',
  'CheckoutBillingDetailsCompleted',
  'InitiateCheckout',
  'Purchase',
]);

/**
 * Facebook/Meta Pixel integration (client-side).
 *
 * This service centralizes:
 * - consent-aware pixel initialization (script injection + fbq bootstrap)
 * - standard `fbq('track', ...)` events
 * - custom `fbq('trackCustom', ...)` events for Speakable-specific funnels
 *
 * The service is intentionally defensive:
 * - does nothing on the server (SSR) where `window` is undefined
 * - does not initialize unless marketing consent is granted (`window.__cmpConsentState`)
 * - initialization is idempotent
 */

class FacebookPixelService {
  /**
   * Create the service instance.
   *
   * The pixel id can come from env, but we also keep a fallback id for environments where the env var
   * isn't provided (e.g. local builds).
   */
  constructor() {
    this.pixelId = process.env.REACT_APP_FACEBOOK_PIXEL_ID || '4200941486856106';
    this.isInitialized = false;
    this.isLoaded = false;
  }

  /**
   * Generate a unique event ID for Pixel+CAPI deduplication.
   * @returns {string}
   */
  generateEventId() {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Drop empty values before pushing tracking metadata to GTM/Meta.
   * @param {object} value
   * @returns {object}
   */
  compact(value = {}) {
    return Object.entries(value).reduce((acc, [key, item]) => {
      if (item === undefined || item === null || item === '') return acc;
      acc[key] = item;
      return acc;
    }, {});
  }

  buildStoredFunnelCustomData(parameters = {}, options = {}) {
    const privateFunnelData = options.funnelCustomData && typeof options.funnelCustomData === 'object'
      ? options.funnelCustomData
      : {};
    return this.compact(stripIdentifyingMarketingData({
      ...parameters,
      ...privateFunnelData,
    }));
  }

  withEventIdAliases(eventId, payload = {}) {
    return this.compact({
      ...payload,
      eventId,
      event_id: eventId,
      eventID: eventId,
    });
  }

  /**
   * Build page-view metadata shared with GTM/server-side tagging.
   * @param {string} pageName
   * @param {object} additionalData
   * @param {string} eventId
   * @returns {{ pixelParameters: object, dataLayerPayload: object }}
   */
  buildPageViewPayload(pageName, additionalData = {}, eventId = this.generateEventId()) {
    const rawPageLocation = typeof window !== 'undefined' ? window.location.href : '';
    const pageLocation = sanitizeMarketingUrl(rawPageLocation);
    let pagePath = '';
    try {
      const safeUrl = new URL(pageLocation || rawPageLocation, 'https://speakable.online');
      pagePath = `${safeUrl.pathname}${safeUrl.search}`;
    } catch (error) {
      pagePath = typeof window !== 'undefined' ? window.location.pathname || '' : '';
    }
    const pageTitle = typeof document !== 'undefined' ? document.title : '';
    const safeAdditionalData = stripIdentifyingMarketingData(additionalData);
    let attribution = {};

    try {
      const locationSearch = typeof window !== 'undefined' ? window.location?.search || '' : '';
      const snapshot = readAttributionSnapshot(locationSearch);
      const earlyMeta = typeof window !== 'undefined' ? window.__speakableMetaAttribution || {} : {};
      attribution = this.compact({
        fbc: snapshot?.fbc || earlyMeta.fbc,
        fbp: snapshot?.fbp || earlyMeta.fbp,
        fbclid: snapshot?.fbclid || earlyMeta.fbclid,
        event_source_url: sanitizeMarketingUrl(snapshot?.sourceUrl || earlyMeta.event_source_url || pageLocation),
        ga_client_id: snapshot?.gaClientId,
        ga_session_id: snapshot?.gaSessionId,
        visitor_id: snapshot?.visitorId,
      });
    } catch (e) {
      attribution = {};
    }

    const pixelParameters = this.compact({
      page_name: pageName,
      page_location: pageLocation,
      page_path: pagePath,
      page_title: pageTitle,
      ...safeAdditionalData,
    });

    const dataLayerPayload = this.compact({
      event: 'page_view',
      event_id: eventId,
      page_name: pageName,
      page_location: pageLocation,
      page_path: pagePath,
      page_title: pageTitle,
      page_referrer: typeof document !== 'undefined' ? sanitizeMarketingUrl(document.referrer || '') : '',
      ...attribution,
      ...safeAdditionalData,
    });

    return { pixelParameters, dataLayerPayload };
  }

  /**
   * Push page-view context for GTM/server-side tags without exposing secrets.
   * @param {string} pageName
   * @param {object} additionalData
   * @param {string} eventId
   */
  pushPageViewDataLayer(pageName, additionalData = {}, eventId = this.generateEventId()) {
    if (typeof window === 'undefined' || !window.dataLayer?.push) return;
    const { dataLayerPayload } = this.buildPageViewPayload(pageName, additionalData, eventId);
    window.dataLayer.push(dataLayerPayload);
  }

  /**
   * Browser tracking must not scrape contact or profile data from storage.
   * Any consented identity matching belongs to an explicit server-side flow.
   * @returns {object}
   */
  getServerMatchingData() {
    return {};
  }

  /**
   * Mirror high-quality PageView events to backend CAPI with the same event_id.
   * We only send when fbc/fbclid exists, so server PageView quality is improved
   * without creating more low-match PageView events.
   * @param {string} pageName
   * @param {object} dataLayerPayload
   * @param {string} eventId
   */
  sendServerPageView(pageName, dataLayerPayload = {}, eventId) {
    if (!eventId || typeof window === 'undefined' || !this.hasMarketingConsent()) return;

    const clickIds = this.getClickIds();
    const fbc = dataLayerPayload.fbc || clickIds.fbc || '';
    const fbp = dataLayerPayload.fbp || clickIds.fbp || '';
    const fbclid = dataLayerPayload.fbclid || '';

    if (!fbc && !fbclid) return;

    const consent = window.__cmpConsentState || null;
    const payload = this.withEventIdAliases(eventId, {
      eventId,
      pageName,
      pageLocation: sanitizeMarketingUrl(dataLayerPayload.page_location || window.location.href),
      pagePath: dataLayerPayload.page_path || window.location.pathname || '',
      pageTitle: dataLayerPayload.page_title || document.title || '',
      fbc,
      fbp,
      fbclid,
      clickIds: fbclid ? { fbclid } : undefined,
      sourceUrl: sanitizeMarketingUrl(dataLayerPayload.event_source_url || dataLayerPayload.page_location || window.location.href),
      consent,
      ...this.getServerMatchingData(),
    });

    instance.post('track/page-view', payload).catch(() => {});
  }

  /**
   * Mirror a browser ViewContent event to backend CAPI with the same event_id.
   * This keeps Meta deduplication intact and improves ViewContent CAPI coverage
   * across every caller of trackViewContent().
   *
   * @param {object} parameters
   * @param {string} eventId
   * @param {{ server?: boolean, sourceUrl?: string }} [options]
   */
  sendServerViewContent(parameters = {}, eventId, options = {}) {
    if (!eventId || typeof window === 'undefined' || !this.hasMarketingConsent()) return;
    if (options.server === false || options.disableServer === true) return;

    const safeParameters = stripIdentifyingMarketingData(parameters);

    let snapshot = {};
    try {
      snapshot = readAttributionSnapshot(window.location?.search || '') || {};
    } catch (e) {
      snapshot = {};
    }

    const pixelIds = this.getClickIds();
    const clickIds = snapshot.clickIds || {};
    const fbclid = safeParameters.fbclid || options.fbclid || clickIds.fbclid || snapshot.fbclid || '';
    const fbc = safeParameters.fbc || options.fbc || snapshot.fbc || pixelIds.fbc || '';
    const fbp = safeParameters.fbp || options.fbp || snapshot.fbp || pixelIds.fbp || '';
    const sourceUrl = sanitizeMarketingUrl(options.sourceUrl || safeParameters.event_source_url || snapshot.sourceUrl || window.location.href);
    const matchingData = this.getServerMatchingData();
    const consent = window.__cmpConsentState || snapshot.consent || null;

    const payload = this.withEventIdAliases(eventId, {
      eventId,
      contentName: safeParameters.content_name || safeParameters.contentName || options.contentName,
      contentCategory: safeParameters.content_category || safeParameters.contentCategory || options.contentCategory,
      contentIds: safeParameters.content_ids || safeParameters.contentIds || options.contentIds,
      contentType: safeParameters.content_type || safeParameters.contentType || options.contentType,
      value: safeParameters.value,
      currency: safeParameters.currency,
      fbc,
      fbp,
      fbclid,
      clickIds: Object.keys(clickIds).length ? clickIds : (fbclid ? { fbclid } : undefined),
      sourceUrl,
      consent,
      ...matchingData,
    });

    instance.post('track/view-content', payload).catch(() => {});
  }

  getDeviceInfo() {
    if (typeof window === 'undefined') return { type: 'unknown' };
    const width = Number(window.innerWidth || 0);
    const height = Number(window.innerHeight || 0);
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
    const type = /ipad|tablet/i.test(ua)
      ? 'tablet'
      : (/android|iphone|mobile|ipod/i.test(ua) || (width > 0 && width < 768))
        ? 'mobile'
        : 'desktop';

    return {
      type,
      viewportWidth: width || undefined,
      viewportHeight: height || undefined,
      userAgent: ua,
    };
  }

  buildFunnelTrackingMeta(snapshot = {}) {
    const pixelIds = this.getClickIds();
    const clickIds = snapshot.clickIds || {};
    return this.compact({
      fbc: snapshot.fbc || pixelIds.fbc,
      fbp: snapshot.fbp || pixelIds.fbp,
      fbclid: snapshot.fbclid || clickIds.fbclid,
      clickIds,
      gaClientId: snapshot.gaClientId,
      gaSessionId: snapshot.gaSessionId,
      visitorId: snapshot.visitorId,
      sourceUrl: sanitizeMarketingUrl(snapshot.sourceUrl || (typeof window !== 'undefined' ? window.location.href : '')),
      userAgent: snapshot.userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : ''),
      consent: snapshot.consent || (typeof window !== 'undefined' ? window.__cmpConsentState || null : null),
      utm: snapshot.utm,
    });
  }

  sendFunnelEvent(eventName, parameters = {}, eventId, options = {}) {
    if (!eventId || typeof window === 'undefined') return;
    if (!STORED_FUNNEL_EVENTS.has(eventName)) return;
    if (options.funnel === false || options.disableFunnel === true) return;
    if (!this.hasAnalyticsConsent()) return;

    let snapshot = {};
    try {
      snapshot = readAttributionSnapshot(window.location?.search || '') || {};
    } catch (e) {
      snapshot = {};
    }

    const safeParameters = stripIdentifyingMarketingData(parameters);
    const trackingMeta = this.buildFunnelTrackingMeta(snapshot);
    const utm = snapshot.utm || {};
    const payload = this.withEventIdAliases(eventId, {
      eventName,
      eventId,
      visitorId: snapshot.visitorId || trackingMeta.visitorId,
      studentId: options.studentId || safeParameters.student_id || safeParameters.studentId,
      bookingId: options.bookingId || safeParameters.booking_id || safeParameters.bookingId,
      source: options.source || safeParameters.source || safeParameters.source_context || utm.source,
      medium: options.medium || utm.medium,
      campaign: options.campaign || utm.campaign,
      content: options.content || safeParameters.utm_content || safeParameters.content_name || utm.content,
      term: options.term || utm.term,
      device: this.getDeviceInfo(),
      url: sanitizeMarketingUrl(options.sourceUrl || safeParameters.event_source_url || snapshot.sourceUrl || window.location.href),
      trackingMeta,
      consent: trackingMeta.consent,
      customData: this.buildStoredFunnelCustomData(safeParameters, options),
    });

    instance.post('v1/tracking/funnel-event', payload).catch(() => {});
  }

  /**
   * Mirror high-intent browser events to backend CAPI with the same event_id.
   * This is intentionally limited to funnel/checkout intent events so noisy
   * engagement events do not dilute Meta event quality.
   *
   * @param {string} eventName
   * @param {object} parameters
   * @param {string} eventId
   * @param {{ server?: boolean, sourceUrl?: string }} [options]
   */
  sendServerCustomEvent(eventName, parameters = {}, eventId, options = {}) {
    if (!eventId || typeof window === 'undefined' || !this.hasMarketingConsent()) return;
    if (options.server === false || options.disableServer === true) return;

    const safeParameters = stripIdentifyingMarketingData(parameters);
    let snapshot = {};
    try {
      snapshot = readAttributionSnapshot(window.location?.search || '') || {};
    } catch (e) {
      snapshot = {};
    }

    const pixelIds = this.getClickIds();
    const clickIds = snapshot.clickIds || {};
    const fbclid = safeParameters.fbclid || options.fbclid || clickIds.fbclid || snapshot.fbclid || '';
    const fbc = safeParameters.fbc || options.fbc || snapshot.fbc || pixelIds.fbc || '';
    const fbp = safeParameters.fbp || options.fbp || snapshot.fbp || pixelIds.fbp || '';
    const matchingData = this.getServerMatchingData();

    const sourceUrl = sanitizeMarketingUrl(options.sourceUrl || safeParameters.event_source_url || snapshot.sourceUrl || window.location.href);
    const consent = window.__cmpConsentState || snapshot.consent || null;

    const payload = this.withEventIdAliases(eventId, {
      eventName,
      eventId,
      customData: this.compact(safeParameters),
      fbc,
      fbp,
      fbclid,
      clickIds: Object.keys(clickIds).length ? clickIds : (fbclid ? { fbclid } : undefined),
      sourceUrl,
      consent,
      ...matchingData,
    });

    instance.post('track/custom-event', payload).catch(() => {});
  }

  /**
   * Read _fbc and _fbp cookies for CAPI deduplication.
   * @returns {{ fbc: string|null, fbp: string|null }}
   */
  getClickIds() {
    if (typeof document === 'undefined') return { fbc: null, fbp: null };
    try {
      const cookies = document.cookie.split(';').reduce((acc, c) => {
        const [k, ...v] = c.trim().split('=');
        acc[k] = v.join('=');
        return acc;
      }, {});
      return {
        fbc: cookies['_fbc'] || null,
        fbp: cookies['_fbp'] || null,
      };
    } catch (e) {
      return { fbc: null, fbp: null };
    }
  }

  /**
   * Set Data Processing Options for Limited Data Use (LDU).
   * Call after init for users in regions requiring LDU (e.g., California).
   * @param {string[]} options - e.g., ['LDU'] or []
   * @param {number} country - 0=auto, 1=US
   * @param {number} state - 0=auto, 1000=California
   */
  setDataProcessingOptions(options = [], country = 0, state = 0) {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('dataProcessingOptions', options, country, state);
    }
  }

  /**
   * Return whether current consent state allows marketing/ads tracking.
   *
   * This mirrors the check in `src/App.js` so we never bootstrap the pixel before consent.
   *
   * @returns {boolean}
   */
  hasMarketingConsent() {
    if (typeof window === 'undefined') return false;
    const consent = window.__cmpConsentState || null;
    return (
      consent?.ad_storage === 'granted' ||
      consent?.personalization_storage === 'granted'
    );
  }

  /**
   * Return whether current consent state allows first-party funnel diagnostics.
   *
   * This is intentionally broader than Meta/ads consent: internal
   * MarketingFunnelEvent storage should work with analytics consent, while
   * Pixel and CAPI calls stay gated by marketing consent.
   *
   * @returns {boolean}
   */
  hasAnalyticsConsent() {
    if (typeof window === 'undefined') return false;
    const consent = window.__cmpConsentState || null;
    return (
      consent?.analytics_storage === 'granted' ||
      consent?.analyticsStorage === 'granted' ||
      this.hasMarketingConsent()
    );
  }

  canUseBrowserPixel() {
    return (
      typeof window !== 'undefined' &&
      this.isInitialized &&
      typeof window.fbq === 'function'
    );
  }

  /**
   * Initialize Facebook Pixel (idempotent).
   *
   * Side effects:
   * - creates `window.fbq` stub (if needed)
   * - injects the pixel script into the page
   * - calls `fbq('init', id)` and emits initial `PageView`
   *
   * If consent is not granted, this method is a no-op.
   *
   * @param {string|null} pixelId Optional override (useful for tests).
   */
  init(pixelId = null) {
    if (this.isInitialized) return;

    const id = pixelId || this.pixelId;
    
    if (!id) {
      console.warn('Facebook Pixel ID not provided');
      return;
    }

    // Create fbq function if it doesn't exist
    if (typeof window !== 'undefined') {
      // Respect consent mode: never initialize before marketing consent.
      if (!this.hasMarketingConsent()) {
        return;
      }

      window.fbq = window.fbq || function() {
        window.fbq.callMethod
          ? window.fbq.callMethod.apply(window.fbq, arguments)
          : window.fbq.queue.push(arguments);
      };
      window._fbq = window._fbq || window.fbq;
      window.fbq.push = window.fbq;
      window.fbq.loaded = true;
      window.fbq.version = '2.0';
      window.fbq.queue = [];

      // Load Facebook Pixel script
      const script = document.createElement('script');
      script.async = true;
      // First-party serving in production (nginx proxy), CDN fallback in dev
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      script.src = isLocal ? 'https://connect.facebook.net/en_US/fbevents.js' : '/fbevents.js';
      // Mark that the external pixel library finished loading (useful for debugging/diagnostics).
      /**
       * Executes logic in `onload`.
       * @returns {void} No return value.
       */
      script.onload = () => {
        this.isLoaded = true;
      };
      document.head.appendChild(script);

      // Do not read profile or contact data from browser storage for matching.
      window.fbq('init', id);

      // Fire initial PageView event (required for Meta Pixel Helper detection)
      const pageViewEventId = this.generateEventId();
      const { pixelParameters, dataLayerPayload } = this.buildPageViewPayload('initial_page_view', {}, pageViewEventId);
      window.fbq('track', 'PageView', pixelParameters, { eventID: pageViewEventId });
      this.pushPageViewDataLayer('initial_page_view', {}, pageViewEventId);
      this.sendFunnelEvent('PageView', { page_name: 'initial_page_view' }, pageViewEventId);
      this.sendServerPageView('initial_page_view', dataLayerPayload, pageViewEventId);
      
      
      this.isInitialized = true;
    }
  }

  /**
   * Track a standard Meta Pixel event.
   *
   * @param {string} eventName
   * @param {object} [parameters]
   */
  track(eventName, parameters = {}, options = {}) {
    if (typeof window === 'undefined') return;
    const canTrackMarketing = this.hasMarketingConsent();
    const canTrackFunnel = this.hasAnalyticsConsent() && STORED_FUNNEL_EVENTS.has(eventName);
    if (!canTrackMarketing && !canTrackFunnel) return;
    const eventId = options.eventId || this.generateEventId();
    const safeParameters = stripIdentifyingMarketingData(parameters);
    if (canTrackMarketing && this.canUseBrowserPixel()) {
      window.fbq('track', eventName, safeParameters, { eventID: eventId });
    }
    return eventId;
  }

  /**
   * Track a custom Meta Pixel event.
   *
   * @param {string} eventName
   * @param {object} [parameters]
   * @param {{ eventId?: string }} [options]
   */
  trackCustom(eventName, parameters = {}, options = {}) {
    if (typeof window === 'undefined') return;
    const canTrackMarketing = this.hasMarketingConsent();
    const canTrackFunnel = this.hasAnalyticsConsent() && STORED_FUNNEL_EVENTS.has(eventName);
    if (!canTrackMarketing && !canTrackFunnel) return;
    const eventId = options.eventId || this.generateEventId();
    const safeParameters = stripIdentifyingMarketingData(parameters);
    if (canTrackMarketing && this.canUseBrowserPixel()) {
      window.fbq('trackCustom', eventName, safeParameters, { eventID: eventId });
    }
    if (canTrackFunnel) this.sendFunnelEvent(eventName, safeParameters, eventId, options);
    if (canTrackMarketing && SERVER_MIRRORED_CUSTOM_EVENTS.has(eventName)) {
      this.sendServerCustomEvent(eventName, safeParameters, eventId, options);
    }
    return eventId;
  }

  /**
   * Convenience wrapper for the standard `ViewContent` event.
   * @param {object} [parameters]
   */
  trackViewContent(parameters = {}, options = {}) {
    const safeParameters = stripIdentifyingMarketingData(parameters);
    const eventId = this.track('ViewContent', safeParameters, options);
    if (eventId) {
      this.sendFunnelEvent('ViewContent', safeParameters, eventId, options);
      this.sendServerViewContent(safeParameters, eventId, options);
    }
    return eventId;
  }

  /**
   * Convenience wrapper for the standard `AddToCart` event.
   * @param {object} [parameters]
   */
  trackAddToCart(parameters = {}) {
    this.track('AddToCart', parameters);
  }

  /**
   * Convenience wrapper for the standard `InitiateCheckout` event.
   * @param {object} [parameters]
   */
  trackInitiateCheckout(parameters = {}, options = {}) {
    const safeParameters = stripIdentifyingMarketingData(parameters);
    const eventId = this.track('InitiateCheckout', safeParameters, options);
    if (eventId) {
      this.sendFunnelEvent('CheckoutStarted', safeParameters, eventId, options);
      this.sendServerCustomEvent('InitiateCheckout', safeParameters, eventId, options);
    }
    return eventId;
  }

  /**
   * Convenience wrapper for the standard `Purchase` event.
   * @param {object} [parameters]
   */
  trackPurchase(parameters = {}, options = {}) {
    const safeParameters = stripIdentifyingMarketingData(parameters);
    const eventId = this.track('Purchase', safeParameters, options);
    if (eventId) this.sendFunnelEvent('Purchase', safeParameters, eventId, options);
    return eventId;
  }

  /**
   * Convenience wrapper for the standard `Lead` event.
   * @param {object} [parameters]
   */
  trackLead(parameters = {}, options = {}) {
    return this.track('Lead', parameters, options);
  }

  /**
   * Convenience wrapper for the standard `CompleteRegistration` event.
   * @param {object} [parameters]
   */
  trackCompleteRegistration(parameters = {}) {
    this.track('CompleteRegistration', parameters);
  }

  /**
   * Convenience wrapper for the standard `Search` event.
   * @param {object} [parameters]
   */
  trackSearch(parameters = {}) {
    this.track('Search', parameters);
  }

  /**
   * Track a page view with an additional, Speakable-specific payload.
   *
   * This emits one standard `PageView` and pushes the same event id plus
   * attribution context to dataLayer so GTM/sGTM can deduplicate and pass fbc/fbp.
   *
   * @param {string} pageName
   * @param {object} [additionalData]
   */
  trackPageView(pageName, additionalData = {}) {
    if (typeof window === 'undefined') return;
    const canTrackMarketing = this.hasMarketingConsent();
    const canTrackFunnel = this.hasAnalyticsConsent();
    if (!canTrackMarketing && !canTrackFunnel) return;
    const eventId = this.generateEventId();
    const { pixelParameters, dataLayerPayload } = this.buildPageViewPayload(pageName, additionalData, eventId);
    if (canTrackMarketing && this.canUseBrowserPixel()) {
      window.fbq('track', 'PageView', pixelParameters, { eventID: eventId });
    }
    if (canTrackMarketing) this.pushPageViewDataLayer(pageName, additionalData, eventId);
    if (canTrackFunnel) this.sendFunnelEvent('PageView', {
      page_name: pageName,
      ...additionalData,
    }, eventId);
    if (canTrackMarketing) this.sendServerPageView(pageName, dataLayerPayload, eventId);
    return eventId;
  }

  /**
   * Track when a user selects a teacher (top-of-funnel intent signal).
   * @param {string} teacherId
   * @param {string} teacherName
   */
  trackTeacherSelection(teacherId, teacherName) {
    this.trackCustom('TeacherSelected', {
      teacher_id: teacherId,
      teacher_name: teacherName,
      content_category: 'teacher_selection'
    });
  }

  /**
   * Track progress through a booking funnel step.
   * @param {string} step
   * @param {object} [additionalData]
   */
  trackBookingStep(step, additionalData = {}) {
    this.trackCustom('BookingStep', {
      step_name: step,
      content_category: 'booking_funnel',
      ...additionalData
    });
  }

  /**
   * Track calendar interactions (e.g. selecting a date).
   * @param {string} action
   * @param {string|null} [date]
   */
  trackCalendarInteraction(action, date = null) {
    this.trackCustom('CalendarInteraction', {
      action: action,
      selected_date: date,
      content_category: 'calendar'
    });
  }

  /**
   * Track form interactions for UX funnel analysis (focus/blur/submit patterns).
   * @param {string} formField
   * @param {string} [action]
   */
  trackFormInteraction(formField, action = 'focus') {
    this.trackCustom('FormInteraction', {
      form_field: formField,
      action: action,
      content_category: 'form'
    });
  }

  /**
   * Track video interactions related to teacher profile videos.
   * @param {string} action
   * @param {string|null} [teacherId]
   */
  trackVideoInteraction(action, teacherId = null) {
    this.trackCustom('VideoInteraction', {
      action: action,
      teacher_id: teacherId,
      content_category: 'video'
    });
  }

  /**
   * Track when a user starts booking a free trial lesson.
   * @param {string} teacherId
   * @param {string} packageId
   */
  trackTrialBookingStart(teacherId, packageId) {
    this.trackCustom('TrialBookingStart', {
      teacher_id: teacherId,
      package_id: packageId,
      content_category: 'trial_booking',
      value: 0 // Trial is free
    });
  }

  trackBookingDetailsSubmitted(additionalData = {}) {
    return this.trackCustom('BookingDetailsSubmitted', {
      content_category: 'booking_funnel',
      ...additionalData,
    });
  }

  trackBookingFailed(reason, additionalData = {}) {
    const {
      privateFunnelData,
      funnelCustomData,
      crmData,
      ...publicAdditionalData
    } = additionalData || {};
    const privateStoredData = this.compact({
      ...(privateFunnelData && typeof privateFunnelData === 'object' ? privateFunnelData : {}),
      ...(funnelCustomData && typeof funnelCustomData === 'object' ? funnelCustomData : {}),
      ...(crmData && typeof crmData === 'object' ? crmData : {}),
    });
    const options = Object.keys(privateStoredData).length
      ? { funnelCustomData: privateStoredData }
      : {};
    const failureReason = publicAdditionalData.failure_reason || reason || 'unknown';
    return this.trackCustom('BookingFailed', {
      failure_reason: failureReason,
      reasonCode: publicAdditionalData.reasonCode || failureReason,
      stage: publicAdditionalData.stage || 'booking_create',
      content_category: 'booking_funnel',
      ...publicAdditionalData,
    }, options);
  }

  trackPackageOfferViewed(additionalData = {}) {
    return this.trackCustom('PackageOfferViewed', {
      content_category: 'post_trial_offer',
      ...additionalData,
    });
  }

  /**
   * Track completion of the trial booking flow.
   *
   * This emits a standard `Lead` event plus canonical `TrialBooked` metadata.
   *
   * @param {{ bookingId?: string, teacherId?: string, packageId?: string }} [bookingData]
   */
  trackTrialBookingComplete(bookingData = {}) {
    this.trackLead({
      content_category: 'trial_booking',
      value: 0,
      currency: 'PLN'
    });
    
    this.trackCustom('TrialBooked', {
      booking_id: bookingData.bookingId,
      teacher_id: bookingData.teacherId,
      package_id: bookingData.packageId,
      selected_date: bookingData.selectedDate,
      selected_time: bookingData.selectedTime,
      user_timezone: bookingData.userTimezone || bookingData.timezone,
      content_category: 'trial_booking'
    });
  }

  /**
   * Track generic UI engagement events.
   * @param {string} action
   * @param {string} element
   * @param {object} [additionalData]
   */
  trackUserEngagement(action, element, additionalData = {}) {
    this.trackCustom('UserEngagement', {
      action: action,
      element: element,
      content_category: 'engagement',
      ...additionalData
    });
  }

  /**
   * Track when the user changes language.
   * @param {string} fromLang
   * @param {string} toLang
   */
  trackLanguageChange(fromLang, toLang) {
    this.trackCustom('LanguageChange', {
      from_language: fromLang,
      to_language: toLang,
      content_category: 'localization'
    });
  }

  /**
   * Track OTP verification events (sent/verified/failed).
   * @param {string} status
   */
  trackOTPVerification(status) {
    if (status === 'verified') {
      this.trackCustom('OTPVerified', {
        status,
        content_category: 'authentication'
      });
      return;
    }

    this.trackCustom('OTPVerification', {
      status: status, // 'sent', 'verified', 'failed'
      content_category: 'authentication'
    });
  }

  /**
   * Track modal open/close/submit events for UX analysis.
   * @param {string} modalName
   * @param {string} action
   */
  trackModalInteraction(modalName, action) {
    this.trackCustom('ModalInteraction', {
      modal_name: modalName,
      action: action, // 'open', 'close', 'submit'
      content_category: 'modal'
    });
  }

  /**
   * Track when a user selects a lesson time slot.
   * @param {string} date
   * @param {string} time
   * @param {string} teacherId
   */
  trackTimeSlotSelection(date, time, teacherId) {
    this.trackCustom('TimeSlotSelected', {
      selected_date: date,
      selected_time: time,
      teacher_id: teacherId,
      content_category: 'scheduling'
    });
  }

  /**
   * Track errors as custom events (best-effort).
   * @param {string} errorType
   * @param {string} errorMessage
   * @param {object} [context]
   */
  trackErrorEvent(errorType, errorMessage, context = {}) {
    this.trackCustom('Error', {
      error_type: errorType,
      error_message: errorMessage,
      content_category: 'error',
      ...context
    });
  }

  /**
   * Track a landing page view with metadata about the landing variant.
   * @param {string} landingType
   * @param {string|null} [teacherId]
   */
  trackLandingPageView(landingType, teacherId = null) {
    this.trackPageView(`landing_${landingType}`, {
      teacher_id: teacherId,
      landing_type: landingType
    });
  }

  /**
   * Track clicks on primary CTAs (call-to-action) across landing pages.
   * @param {string} ctaName
   * @param {string} position
   * @param {object} [additionalData]
   */
  trackCTAClick(ctaName, position, additionalData = {}) {
    this.trackCustom('CTAClick', {
      cta_name: ctaName,
      cta_position: position,
      content_category: 'cta',
      ...additionalData
    });
  }

  /**
   * Track when a user sees a social proof element (testimonial, viewer count, urgency timer, etc.).
   * @param {string} proofType
   * @param {any} [value]
   */
  trackSocialProof(proofType, value = null) {
    this.trackCustom('SocialProofView', {
      proof_type: proofType, // 'testimonial', 'viewer_count', 'timer'
      proof_value: value,
      content_category: 'social_proof'
    });
  }

  /**
   * Track urgency timer impressions/interactions.
   * @param {string} timerType
   * @param {any} [timeRemaining]
   */
  trackUrgencyTimer(timerType, timeRemaining = null) {
    this.trackCustom('UrgencyTimer', {
      timer_type: timerType,
      time_remaining: timeRemaining,
      content_category: 'urgency'
    });
  }

  /**
   * Track exit intent interactions (popup triggered/dismissed/converted).
   * @param {string} action
   */
  trackExitIntent(action) {
    this.trackCustom('ExitIntent', {
      action: action, // 'triggered', 'dismissed', 'converted'
      content_category: 'exit_intent'
    });
  }
}

// Create singleton instance
const facebookPixel = new FacebookPixelService();

export default facebookPixel;
